import { useCallback, useState } from 'react';
import type { JSX } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ClipboardCheck,
  ClipboardList,
  Building2,
  Factory,
  FileText,
  Server,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useReportDetailContext } from '../ReportDetailContext';
import type { ReportNavNode, ReportNavigation, ReportTopLevelSection } from '../types';
import { DOCUMENT_ROUTE } from '../constants';

function ReportNavIcon({ name, className }: { name: string; className?: string }) {
  switch (name) {
    case 'description':
      return <FileText className={className} />;
    case 'domain':
      return <Building2 className={className} />;
    case 'factory':
      return <Factory className={className} />;
    case 'fact_check':
      return <ClipboardList className={className} />;
    case 'assignment_turned_in':
      return <ClipboardCheck className={className} />;
    case 'dns':
      return <Server className={className} />;
    default:
      return <FileText className={className} />;
  }
}

export function ReportSideNav({
  navigation,
  onNavigate,
}: {
  navigation: ReportNavigation;
  onNavigate: () => void;
}): JSX.Element {
  const { report, currentSection, activeAnchor } = useReportDetailContext();
  const params = { reportId: report.id };
  const [expandedNodeId, setExpandedNodeId] = useState<ReportTopLevelSection | null>(
    currentSection.topLevel ?? navigation.nodes[0]?.id ?? null
  );

  const resolvedExpandedNodeId = currentSection.topLevel ?? expandedNodeId;

  return (
    <nav className="flex h-full flex-col p-3" aria-label="Navigazione sezione report">
      <div className="space-y-1 flex-1 overflow-y-auto">
        {navigation.nodes.map((node) => (
          <SideNavSection
            key={node.id}
            node={node}
            active={currentSection.topLevel === node.id}
            params={params}
            currentHash={activeAnchor}
            onNavigate={onNavigate}
            expanded={resolvedExpandedNodeId === node.id}
            onExpand={setExpandedNodeId}
          />
        ))}
      </div>
    </nav>
  );
}

function SideNavSection({
  node,
  active,
  params,
  currentHash,
  onNavigate,
  expanded,
  onExpand,
}: {
  node: ReportNavNode;
  active: boolean;
  params: { reportId: string };
  currentHash?: string;
  onNavigate: () => void;
  expanded: boolean;
  onExpand: (id: ReportTopLevelSection) => void;
}) {
  const navigate = useNavigate();

  const handleTopLevelClick = useCallback(() => {
    const targetAnchor = node.sections[0]?.anchor;

    navigate({
      to: DOCUMENT_ROUTE,
      params,
      search: (prev) => ({
        ...prev,
        section: node.id,
        subsection: targetAnchor,
      }),
    });
    onExpand(node.id);
    onNavigate();
  }, [navigate, node, onExpand, onNavigate, params]);

  const ChevronIcon = expanded ? ChevronDown : ChevronRight;

  return (
    <div className="py-0.5">
      <button
        type="button"
        className={cn(
          'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-all rounded-md select-none',
          active
            ? 'text-brand-blue-700 font-bold'
            : 'text-text-secondary font-medium hover:text-text-primary hover:bg-neutral-50'
        )}
        onClick={handleTopLevelClick}
      >
        <ReportNavIcon
          name={node.icon}
          className={cn('h-4 w-4 shrink-0', active ? 'text-brand-blue-600' : 'text-text-tertiary')}
        />
        <span className="flex-1 min-w-0">{node.label}</span>
        <ChevronIcon className="h-3.5 w-3.5 shrink-0 text-text-tertiary opacity-70" />
      </button>

      {expanded && node.sections.length > 0 ? (
        <div className="mt-0.5 ml-5 border-l border-neutral-200 pl-2 space-y-0.5">
          {node.sections.map((section) => (
            <SideNavLink
              key={section.id}
              params={params}
              label={section.label}
              active={active && Boolean(currentHash && section.sectionIds.includes(currentHash))}
              onNavigate={onNavigate}
              variant="anchor"
              search={(prev) => ({
                ...prev,
                section: node.id,
                subsection: section.anchor,
              })}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SideNavLink({
  params,
  search,
  label,
  active,
  onNavigate,
  variant = 'anchor',
}: {
  params: Record<string, string>;
  search?: (prev: Record<string, unknown>) => Record<string, unknown>;
  label: string;
  active: boolean;
  onNavigate: () => void;
  variant?: 'unit' | 'anchor';
}) {
  return (
    <Link
      to={DOCUMENT_ROUTE}
      params={params as never}
      search={search as never}
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 transition-colors select-none rounded-md',
        variant === 'anchor' && [
          'text-sm',
          active
            ? 'text-brand-blue-700 font-medium bg-brand-blue-50'
            : 'text-text-secondary hover:text-text-primary hover:bg-neutral-50',
        ],
        variant === 'unit' && [
          'text-sm font-semibold',
          active ? 'text-brand-blue-700' : 'text-text-secondary hover:text-text-primary',
        ]
      )}
      onClick={onNavigate}
    >
      {variant === 'unit' ? (
        <>
          <Server className="h-4 w-4 text-brand-blue-500" />
          {label}
        </>
      ) : (
        <span className="truncate">{label}</span>
      )}
    </Link>
  );
}
